import {
  AfterViewInit,
  Directive,
  effect,
  inject,
  input,
  Renderer2,
  RendererStyleFlags2,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { OverlayTriggerBase } from '../fc-overlay/overlay-trigger-base.directive';

// todo: full refactor
@Directive({
  selector: '[fcTooltip]',
  host: {
    '(pointerenter)': 'onPointerEnter($event)',
    '(pointerleave)': 'hide()',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut($event)',
    '(click)': 'onClick()',
  },
})
export class TooltipDirective
  extends OverlayTriggerBase
  implements AfterViewInit
{
  readonly text = input<string | null>(null, { alias: 'fcTooltip' });
  readonly visible = input<boolean | null>(null, { alias: 'showTooltip' });
  readonly position = input<'top' | 'bottom' | 'left' | 'right'>('top');
  readonly offset = input<number>(8);
  readonly showTooltipOnClick = input(false);
  readonly showTooltipOnlyWhenTruncated = input(false);

  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private contentEl: HTMLElement | null = null;
  private onDocumentClick = (): void => {
    if (!this.elementRef.nativeElement.matches(':hover')) {
      this.hide();
    }
  };

  constructor() {
    super();
    effect(() => {
      const text = this.text();
      const visible = this.visible();
      const position = this.position();
      const offset = this.offset();
      if (this.contentEl) {
        this.contentEl.textContent = text ?? '';
      }
      if (this.overlayElement) {
        this.renderer.setAttribute(
          this.overlayElement,
          'data-position',
          position,
        );
        this.renderer.setStyle(
          this.overlayElement,
          '--fc-overlay-offset',
          `${offset}px`,
          RendererStyleFlags2.DashCase,
        );
      }
      if (visible === true && this.isTriggerVisible()) {
        this.show();
      } else if (visible === false || !this.isTriggerVisible()) {
        this.hide();
      }
    });
  }

  ngAfterViewInit(): void {
    this.createTooltipElement();
  }

  override ngOnDestroy(): void {
    if (this.overlayElement) {
      this.overlayService.unregister(this.overlayId);
      const parent = this.overlayElement.parentNode;
      if (parent) {
        this.renderer.removeChild(parent, this.overlayElement);
      }
      this.overlayElement = null;
    }
  }

  protected override showOverlay(): void {
    if (!this.overlayElement || this.overlayElement.matches(':popover-open')) {
      return;
    }
    this.overlayElement.showPopover();
    document.addEventListener('click', this.onDocumentClick, true);
  }

  protected override hideOverlay(): void {
    document.removeEventListener('click', this.onDocumentClick, true);
    if (!this.overlayElement) return;
    try {
      this.overlayElement.hidePopover();
    } catch {
      /* already hidden */
    }
  }

  private createTooltipElement(): void {
    const el = this.renderer.createElement('div');
    this.renderer.setAttribute(el, 'id', this.overlayId);
    this.renderer.setAttribute(el, 'popover', 'manual');
    this.renderer.setAttribute(el, 'role', 'tooltip');
    this.renderer.setAttribute(el, 'data-position', this.position());
    this.renderer.addClass(el, 'fc-overlay');
    this.renderer.setStyle(
      el,
      'position-anchor',
      this.anchorName,
      RendererStyleFlags2.DashCase,
    );
    this.renderer.setStyle(
      el,
      '--fc-overlay-offset',
      `${this.offset()}px`,
      RendererStyleFlags2.DashCase,
    );

    const content = this.renderer.createElement('div');
    this.renderer.setAttribute(
      content,
      'class',
      'fc-tooltip-content w-max max-w-xs rounded-lg bg-secondary-900 px-12 py-8 text-xs text-grey-100 shadow-lg',
    );
    this.renderer.setProperty(content, 'textContent', this.text() ?? '');
    this.renderer.appendChild(el, content);

    this.renderer.appendChild(this.document.body, el);
    this.overlayService.register(this.overlayId, el);
    this.overlayElement = el;
    this.contentEl = content;
  }

  protected show(): void {
    if (!this.text() || !this.isTriggerVisible()) return;
    const isHiddenUntilTruncated =
      this.showTooltipOnlyWhenTruncated() && !this.isTriggerTruncated();
    if (isHiddenUntilTruncated) return;
    this.showOverlay();
  }

  protected hide(): void {
    this.hideOverlay();
  }

  protected onPointerEnter(event: PointerEvent): void {
    // Touch taps fire pointerenter/focusin but never a matching leave, and the
    // tapped element keeps matching :hover — the tooltip would stay open
    // forever. Hover tooltips are mouse/pen-only; touch users never see them.
    if (event.pointerType === 'touch') return;
    this.show();
  }

  protected onClick(): void {
    if (this.showTooltipOnClick()) this.show();
  }

  protected onFocusIn(): void {
    // Only keyboard focus should open the tooltip — tap/click focus on touch
    // devices has no focusout until the user taps something else focusable.
    if (!this.isKeyboardFocusWithin()) return;
    this.show();
  }

  protected onFocusOut(event: FocusEvent): void {
    const nextFocused = event.relatedTarget;
    if (
      nextFocused instanceof HTMLElement &&
      this.elementRef.nativeElement.contains(nextFocused)
    ) {
      return;
    }

    this.hide();
  }

  private isTriggerVisible(): boolean {
    const element = this.elementRef.nativeElement;
    return element.getClientRects().length > 0;
  }

  private isTriggerTruncated(): boolean {
    const element = this.elementRef.nativeElement;
    return (
      element.scrollWidth > element.clientWidth ||
      element.scrollHeight > element.clientHeight
    );
  }

  private isKeyboardFocusWithin(): boolean {
    const element = this.elementRef.nativeElement;
    return (
      element.matches(':focus-visible') ||
      element.querySelector(':focus-visible') !== null
    );
  }
}
